import React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components/macro";
import { Tabs, Tab } from "../../../components/Tabs/Tabs";
import { Breadcrumb } from "../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../components/LinkUnstyled/LinkUnstyled";
import {
  Text3xlBold,
  TextSmRegular,
  TextSmMedium,
} from "../../../components/Typography/TypographyV2";
import { PageWrapper, ContentWrapper } from "../../../layout/portalPageLayout";
import useSWR from "swr";
import type { Tenant } from "../../../types/types";
import { ErrorPlaceholder } from "../../../components/Error";
import { UsersList } from "./UsersList/UsersList";
import type { AxiosError } from "axios";
import { useRoutePath } from "../../../util/Routing";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { endpoints } from "../../../endpoints";
import { makeUrlWithParams, useStoreState } from "../../../util/util";
import { CompanyInfo } from "./CompanyInfo/CompanyInfo";
import { CustomerSettings } from "./CustomerSettings/CustomerSettings";
import { PriceTiersTab } from "./PriceTiersTab/PriceTiersTab";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { useTranslation } from "react-i18next";
import { useQuery } from "../../../util/hooks";

type Params = {
  tenant_id: string;
  tenantSlug: string;
};

type PageTabs = "Company Info" | "Settings" | "Users" | "Price Tiers";

const PageTitle = styled(Text3xlBold).attrs({ as: "h1" })`
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const MetadataRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  margin: ${({ theme }) => theme.resolve("spacing_xs")} 0
    ${({ theme }) => theme.resolve("spacing_lg")};
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

/**
 * Customer detail page.  Used for seller admin.
 */
export const CustomerDetailPage = () => {
  const user = useAuthenticatedUser();
  const {
    storefront_id,
    storefront_metadata: { edition },
  } = useStoreState();
  const { tenant_id: buyerTenantId } = useParams<Params>();
  const [query] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const { adminPath } = useRoutePath();
  const { t } = useTranslation();

  const pageTabs: PageTabs[] = [
    "Company Info",
    "Settings",
    "Users",
    "Price Tiers",
  ];
  const urlQuery = useQuery();
  const [tabIndex] = React.useState(() => {
    const maybeTabIndex = urlQuery.get("tab");
    if (maybeTabIndex && pageTabs.includes(maybeTabIndex as PageTabs)) {
      return maybeTabIndex;
    } else return "Company Info";
  });

  const {
    data: buyerTenant,
    error: buyerTenantError,
    mutate: mutateBuyerTenant,
  } = useSWR<Tenant, AxiosError>(
    buyerTenantId
      ? makeUrlWithParams(
          endpoints.v1_storefronts_id_tenants_id(storefront_id, buyerTenantId),
          {
            show_inactive_address: "true",
          }
        )
      : null
  );

  if (buyerTenantError) {
    return <ErrorPlaceholder message={t("Failed to fetch customer.")} />;
  }
  if (!buyerTenant) {
    // Loading.
    return <DelayedSpinner />;
  }

  return (
    <PageWrapper narrow>
      <Breadcrumb>
        <LinkUnstyled
          to={makeUrlWithParams(`${adminPath}/tenants`, {
            q: query.q,
            offset: query.offset,
            perPage: query.perPage,
          })}
        >
          {t("Customers")}
        </LinkUnstyled>
        <div>/</div>
        <div>{buyerTenant.name}</div>
      </Breadcrumb>
      <PageTitle>{buyerTenant.name}</PageTitle>
      {(buyerTenant.crm_id || buyerTenant.external_identity?.external_id) && (
        <MetadataRow>
          {buyerTenant.crm_id && (
            <MetadataItem>
              <TextSmRegular color="secondary">{t("CRM ID")}:</TextSmRegular>
              <TextSmMedium>{buyerTenant.crm_id}</TextSmMedium>
            </MetadataItem>
          )}
          {buyerTenant.external_identity?.external_id && (
            <MetadataItem>
              <TextSmRegular color="secondary">
                {t("Customer ID")}:
              </TextSmRegular>
              <TextSmMedium>
                {buyerTenant.external_identity?.external_id}
              </TextSmMedium>
            </MetadataItem>
          )}
        </MetadataRow>
      )}
      <ContentWrapper>
        <Tabs selectedIndex={tabIndex} allowDeepLink={true}>
          <Tab label={t("Company Info")}>
            <CompanyInfo
              buyerTenant={buyerTenant}
              mutateBuyerTenant={mutateBuyerTenant}
            />
          </Tab>
          <Tab label={t("Users")}>
            <UsersList tenantID={buyerTenant.id} />
          </Tab>
          <Tab label={t("Settings")}>
            <CustomerSettings buyerTenant={buyerTenant} loggedInUser={user} />
          </Tab>
          {edition !== "pim" ? (
            <Tab label={t("Price Tiers")}>
              <PriceTiersTab buyerTenant={buyerTenant} user={user} />
            </Tab>
          ) : (
            <></>
          )}
        </Tabs>
      </ContentWrapper>
    </PageWrapper>
  );
};
